import type { Action, ThunkAction } from "@reduxjs/toolkit";
import { combineSlices, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

import { NgdsSlice } from "../features/ngds/ngdsSlice";
import { UserSlice } from "../features/user/userSlice";
import { LocalisationSlice } from "../features/localization/localisationSlice";
import { ConfigSlice } from "../features/config/configSlice";
import { UISlice } from "../features/ui/uiSlice";
import createSyncMiddleware from "../../middleware/syncMiddleware";
import createSelfWeightMiddleware from "../../middleware/selfWeightMiddleware";
import { ProjectSlice } from "../features/projects/projectSlice";
import { materialSectionSlice } from "../features/materialsSections/materialSectionSlice";
import { unitSlice } from "../features/unit/unitSlice";
import { DesignReviewSlice } from "../features/designReview/designReviewSlice";

// `combineSlices` automatically combines the reducers using
// their `reducerPath`s, therefore we no longer need to call `combineReducers`.
const rootReducer = combineSlices(
  UserSlice,
  ProjectSlice,
  LocalisationSlice,
  ConfigSlice,
  UISlice,
  materialSectionSlice,
  unitSlice,
  DesignReviewSlice,
);
// Infer the `RootState` type from the root reducer
export type RootState = ReturnType<typeof rootReducer>;

// The store setup is wrapped in `makeStore` to allow reuse
// when setting up tests that need the same store config
export const makeStore = (preloadedState?: Partial<RootState>) => {
  const store = configureStore({
    reducer: rootReducer,
    // Adding the api middleware enables caching, invalidation, polling,
    // and other useful features of `rtk-query`.
    middleware: (getDefaultMiddleware) => {
      return getDefaultMiddleware({ serializableCheck: false }).concat(createSyncMiddleware({ debounceTime: 2000 }), createSelfWeightMiddleware()); //
    },
    preloadedState,
  });
  // configure listeners using the provided defaults
  // optional, but required for `refetchOnFocus`/`refetchOnReconnect` behaviors
  setupListeners(store.dispatch);
  return store;
};

export let store = makeStore();
// Infer the type of `store`
export type AppStore = typeof store;
// Infer the `AppDispatch` type from the store itself
export type AppDispatch = AppStore["dispatch"];
export type AppThunk<ThunkReturnType = void> = ThunkAction<ThunkReturnType, RootState, unknown, Action>;
